export interface DocumentHeading {
  id: string
  level: 1 | 2 | 3
  text: string
}

function cleanHeadingText(value: string) {
  return value
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\s+#+\s*$/, '')
    .trim()
}

function headingId(text: string, index: number) {
  const slug = text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
  return slug ? `${slug}-${index}` : `heading-${index}`
}

export function extractMarkdownHeadings(
  markdown?: string | null,
  maxItems = 18,
): DocumentHeading[] {
  if (!markdown) return []

  const headings: DocumentHeading[] = []
  let inFence = false

  for (const line of markdown.split(/\r?\n/)) {
    if (/^\s*```/.test(line)) {
      inFence = !inFence
      continue
    }
    if (inFence) continue

    const match = /^(#{1,3})\s+(.+)$/.exec(line.trim())
    if (!match) continue

    const text = cleanHeadingText(match[2])
    if (!text) continue

    headings.push({
      id: headingId(text, headings.length),
      level: match[1].length as 1 | 2 | 3,
      text,
    })

    if (headings.length >= maxItems) break
  }

  return headings
}

function textFromInlineContent(value: unknown): string {
  if (typeof value === 'string') return value
  if (!Array.isArray(value)) return ''

  return value
    .map((item) => {
      if (typeof item === 'string') return item
      if (!item || typeof item !== 'object') return ''
      const record = item as Record<string, unknown>
      if (typeof record.text === 'string') return record.text
      return textFromInlineContent(record.content)
    })
    .join('')
    .trim()
}

export function extractContentJsonHeadings(contentJson: unknown, maxItems = 18): DocumentHeading[] {
  if (!Array.isArray(contentJson)) return []

  const headings: DocumentHeading[] = []
  const visit = (blocks: unknown[]) => {
    for (const block of blocks) {
      if (!block || typeof block !== 'object') continue

      const record = block as Record<string, unknown>
      if (record.type === 'heading') {
        const props =
          record.props && typeof record.props === 'object'
            ? (record.props as Record<string, unknown>)
            : {}
        const rawLevel = Number(props.level ?? 1)
        const level = rawLevel === 2 || rawLevel === 3 ? rawLevel : 1
        const text = textFromInlineContent(record.content)

        if (text) {
          headings.push({
            id: headingId(text, headings.length),
            level,
            text,
          })
        }
      }

      if (Array.isArray(record.children)) visit(record.children)
      if (headings.length >= maxItems) return
    }
  }

  visit(contentJson)
  return headings.slice(0, maxItems)
}

export function extractDocumentHeadings(
  markdown?: string | null,
  contentJson?: unknown,
  maxItems = 18,
): DocumentHeading[] {
  const markdownHeadings = extractMarkdownHeadings(markdown, maxItems)
  if (markdownHeadings.length > 0) return markdownHeadings
  return extractContentJsonHeadings(contentJson, maxItems)
}
